import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import createTeamConfig from "../../config/createTeamConfig.json";
import { isSingleRaceTeam, VUELTA_TEAM_ID } from "../../config/competitionConfig";
import "../styles/SelectCyclists.css";

const SelectCyclists = ({
  numberPlayerSelected,
  setNumberPlayerSelected,
  budget,
  setBudget,
  selectedCyclists,
  setSelectedCyclists,
  setCyclistsBonus,
  cyclistsBonus,
  cyclists,
  teamId,
  nextStep,
}) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const firstLineCyclists = selectedCyclists.filter((_, index) => index % 2 === 0);
  const secondLineCyclists = selectedCyclists.filter((_, index) => index % 2 !== 0);
  const [cyclistsList, setCyclistsList] = useState(cyclists);
  const [selectedSortValue, setSelectedSortValue] = useState("value");
  const isSingleRace = isSingleRaceTeam(teamId);
  const teamLabel = teamId === 1
    ? "cyclistsWorldTourNumber"
    : teamId === 2
      ? "cyclistsNeoProNumber"
      : teamId === VUELTA_TEAM_ID
        ? "cyclistsVueltaNumber"
      : isSingleRace
        ? "cyclistsGiroNumber"
        : "cyclistsGiroNumber";

  useEffect(() => {
    setCyclistsList(cyclists);
  }, [cyclists]);

  const handleSelectCyclist = (cyclist) => {
    if (selectedCyclists.some((selected) => selected.cyclistId === cyclist.cyclistId)) {
      setSelectedCyclists(
        selectedCyclists.filter((selected) => selected.cyclistId !== cyclist.cyclistId)
      );
      setBudget(budget + cyclist.finalValue);
      setNumberPlayerSelected(numberPlayerSelected - 1);

      if (cyclistsBonus.some((changeable) => changeable.cyclistId === cyclist.cyclistId)) {
        setCyclistsBonus(
          cyclistsBonus.filter((changeable) => changeable.cyclistId !== cyclist.cyclistId)
        );
      }
    } else if (budget >= cyclist.finalValue) {
      setSelectedCyclists([...(selectedCyclists || []), cyclist]);
      setBudget(budget - cyclist.finalValue);
      setNumberPlayerSelected(numberPlayerSelected + 1);
    }
  };

  const isCyclistSelected = (cyclistId) => {
    return selectedCyclists.some((selected) => selected.cyclistId === cyclistId);
  };

  const isButtonDisabled =
    selectedCyclists.length < createTeamConfig[teamLabel].min ||
    selectedCyclists.length > createTeamConfig[teamLabel].max;

  const sortCyclists = (sortBy, list) => {
    const next = [...(list || [])];

    switch (sortBy) {
      case "value":
        next.sort((a, b) => b.finalValue - a.finalValue);
        break;
      case "nation":
        next.sort((a, b) => a.nationality.localeCompare(b.nationality));
        break;
      case "team":
        next.sort((a, b) => a.team.localeCompare(b.team));
        break;
      default:
        break;
    }

    setCyclistsList(next);
  };

  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);

    if (newSearchTerm) {
      const filtered = (cyclists || []).filter((c) =>
        (c.name || "").toLowerCase().startsWith(newSearchTerm.toLowerCase())
      );
      sortCyclists(selectedSortValue, filtered);
    } else {
      sortCyclists(selectedSortValue, cyclists);
    }
  };

  const handleSortChange = (event) => {
    const sortBy = event.target.value;
    setSelectedSortValue(sortBy);
    sortCyclists(sortBy, cyclistsList);
  };

  const flagSrc = (nationality) =>
    `${process.env.PUBLIC_URL}/flags/${(nationality || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ /g, "_")
      .replace(/-/g, "_")
      .toLowerCase()}.png`;

  return (
    <main className="select-cyclists">
      <div className="select-cyclists-antiscroll">
        <div className="select-cyclists-shadow-container">
          <div className="select-cyclists-title">
            <h1>{t("selectCyclists.title")}</h1>
            <div className="select-cyclists-shadow-mask-right"></div>
            <div className="select-cyclists-shadow-mask-bottom"></div>
          </div>
        </div>

        <div className="select-cyclists-top-container">
          <div className="select-cyclists-rules">
            <p>
              {t("selectCyclists.rules", {
                min: createTeamConfig[teamLabel].min,
                max: createTeamConfig[teamLabel].max,
              })}
            </p>
          </div>

          <div className="select-cyclists-header-pannel">
            <div className="select-cyclists-header-pannel-content">
              <p>
                {t("selectCyclists.selectedCount")} :{" "}
                <span>{numberPlayerSelected}</span>
              </p>
            </div>

            <div className="select-cyclists-header-pannel-content">
              <p>
                {t("selectCyclists.remainingBudget")} : <strong>{budget}M</strong>
              </p>
            </div>

            <div className="select-cyclists-next">
              <button onClick={nextStep} disabled={isButtonDisabled}>
                {t("common.validate")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="select-cyclists-pannel-view">
        <div className="select-cyclists-choice">
          <div className="select-cyclists-controls">
            <div className="select-cyclists-sort-control">
              <label>{t("selectCyclists.controls.sortBy")}</label>
              <select onChange={handleSortChange} value={selectedSortValue}>
                <option value="value">{t("selectCyclists.controls.sort.value")}</option>
                <option value="nation">{t("selectCyclists.controls.sort.nation")}</option>
                <option value="team">{t("selectCyclists.controls.sort.team")}</option>
              </select>
            </div>

            <div className="select-cyclists-search-control">
              <img
                src={`${process.env.PUBLIC_URL}/icons/loupe.png`}
                alt={t("selectCyclists.controls.searchIconAlt")}
                width="22"
                height="22"
                className="select-cyclists-search-icon"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={t("selectCyclists.controls.searchPlaceholder")}
              />
            </div>
          </div>

          <div className="select-cyclists-table-container">
            <table>
              <thead>
                <tr>
                  <th>{t("selectCyclists.table.cyclist")}</th>
                  <th>{t("selectCyclists.table.team")}</th>
                  <th>{t("selectCyclists.table.nation")}</th>
                  <th>{t("selectCyclists.table.value")}</th>
                  <th>{t("selectCyclists.table.speciality")}</th>
                </tr>
              </thead>

              <tbody>
                {cyclistsList.map((cyclist) => (
                  <tr
                    key={cyclist.cyclistId}
                    onClick={() => handleSelectCyclist(cyclist)}
                    className={
                      isCyclistSelected(cyclist.cyclistId)
                        ? "select-cyclists-table-selected-row"
                        : ""
                    }
                  >
                    <td>
                      <div className="select-cyclists-name-info">
                        <img
                          className="select-cyclists-flag-icon"
                          src={flagSrc(cyclist.nationality)}
                          alt={t("selectCyclists.flagAlt", { nationality: cyclist.nationality })}
                          width="20"
                          height="20"
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                        />
                        <span>{cyclist.name}</span>
                      </div>
                    </td>
                    <td>{cyclist.team}</td>
                    <td>{cyclist.nationality}</td>
                    <td>{cyclist.finalValue}</td>
                    <td>{cyclist.speciality}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="select-cyclists-choice-view">
          <div className="select-cyclists-scroll-container">
            <div className="select-cyclists-line-container">
              {firstLineCyclists.map((cyclist) => (
                <div className="select-cyclists-selected-cell" key={cyclist.cyclistId}>
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/cancel.png`}
                    className="select-cyclists-cancel-icon"
                    alt={t("selectCyclists.removeAlt")}
                    width="17"
                    height="17"
                    onClick={() => handleSelectCyclist(cyclist)}
                  />
                  <div className="select-cyclists-selected-info">
                    <h4>
                      <img
                        className="select-cyclists-flag-icon"
                        src={flagSrc(cyclist.nationality)}
                        alt={t("selectCyclists.flagAlt", { nationality: cyclist.nationality })}
                        width="20"
                        height="20"
                        onError={(e) => {
                          e.currentTarget.style.visibility = "hidden";
                        }}
                      />
                      {cyclist.name}
                    </h4>
                    <p>{cyclist.team}</p>
                    <p>
                      {t("selectCyclists.valueLabel")} :{" "}
                      <strong>{cyclist.finalValue}</strong>
                    </p>
                    <div
                      style={{
                        width: "100%",
                        height: "15px",
                        backgroundColor: cyclist.teamColor,
                        marginTop: "35px",
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="select-cyclists-line-container">
              {secondLineCyclists.map((cyclist) => (
                <div className="select-cyclists-selected-cell" key={cyclist.cyclistId}>
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/cancel.png`}
                    className="select-cyclists-cancel-icon"
                    alt={t("selectCyclists.removeAlt")}
                    width="17"
                    height="17"
                    onClick={() => handleSelectCyclist(cyclist)}
                  />
                  <div className="select-cyclists-selected-info">
                    <h4>
                      <img
                        className="select-cyclists-flag-icon"
                        src={flagSrc(cyclist.nationality)}
                        alt={t("selectCyclists.flagAlt", { nationality: cyclist.nationality })}
                        width="20"
                        height="20"
                        onError={(e) => {
                          e.currentTarget.style.visibility = "hidden";
                        }}
                      />
                      {cyclist.name}
                    </h4>
                    <p>{cyclist.team}</p>
                    <p>
                      {t("selectCyclists.valueLabel")} :{" "}
                      <strong>{cyclist.finalValue}</strong>
                    </p>
                    <div
                      style={{
                        width: "100%",
                        height: "15px",
                        backgroundColor: cyclist.teamColor,
                        marginTop: "35px",
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SelectCyclists;
